import { TooltipProvider } from '@parsleyai/ui'
import type { ReactNode } from 'react'
import { AccountSettingsProvider } from './accountSettings/AccountSettingsContext'
import { AnalyticsConsentProvider } from './analyticsConsent/AnalyticsConsentContext'
import { AuthProvider } from './auth/AuthContext'
import { FeatureFlagsProvider } from './featureFlags/FeatureFlagsContext'
import { FeedbackNudgeProvider } from './feedbackNudge/FeedbackNudgeContext'
import { PlanGenProvider } from './planGen/PlanGenContext'
import { PrimaryActionProvider } from './primaryAction/PrimaryActionContext'
import { ProfileProvider } from './profile/ProfileContext'
import { IngredientsRefProvider } from './referenceData/ingredients/IngredientsRefContext'
import { ThemeProvider } from './theme/ThemeContext'
import { UndoProvider } from './undo/UndoProvider'
import { BillingProvider } from './userData/billing/BillingContext'
import { EatingPreferencesProvider } from './userData/eatingPreferences/EatingPreferencesContext'
import { FoodProfilesProvider } from './userData/foodProfiles/FoodProfilesContext'
import { MealLibraryProvider } from './userData/mealLibrary/MealLibraryContext'
import { MealLogProvider } from './userData/mealLog/MealLogContext'
import { MealPlanRequestsProvider } from './userData/mealPlanRequests/MealPlanRequestsContext'
import { MealPlanResultsProvider } from './userData/mealPlanResults/MealPlanResultsContext'
import { MealsProvider } from './userData/meals/MealsContext'
import { PreferencesProvider } from './userData/preferences/PreferencesContext'
import { UserDataProvider } from './userData/UserDataContext'

interface StoreProviderProps {
  children: ReactNode
}

export function StoreProvider({ children }: StoreProviderProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <AuthProvider>
        <ThemeProvider>
          {/* Global and auth-gated, like the ingredient reference data — nothing below it is a dependency. */}
          <FeatureFlagsProvider>
            <IngredientsRefProvider>
              <UserDataProvider>
                <MealsProvider>
                  <ProfileProvider>
                    {/* Needs only `uid`, and sits high so the plan-generation gates below can read entitlement. */}
                    <BillingProvider>
                      <EatingPreferencesProvider>
                        <FoodProfilesProvider>
                          <MealPlanRequestsProvider>
                            <MealPlanResultsProvider>
                              {/* Recipes library — its hook joins meals + user state with cook activity from the cook log, so it nests inside MealLogProvider. */}
                              <MealLogProvider>
                                <MealLibraryProvider>
                                  <PreferencesProvider>
                                    {/* Consumes PreferencesContext (owns the privacy mirror) — must nest inside it. */}
                                    <AnalyticsConsentProvider>
                                      <AccountSettingsProvider>
                                        <PlanGenProvider>
                                          <UndoProvider>
                                            <FeedbackNudgeProvider>
                                              <PrimaryActionProvider>{children}</PrimaryActionProvider>
                                            </FeedbackNudgeProvider>
                                          </UndoProvider>
                                        </PlanGenProvider>
                                      </AccountSettingsProvider>
                                    </AnalyticsConsentProvider>
                                  </PreferencesProvider>
                                </MealLibraryProvider>
                              </MealLogProvider>
                            </MealPlanResultsProvider>
                          </MealPlanRequestsProvider>
                        </FoodProfilesProvider>
                      </EatingPreferencesProvider>
                    </BillingProvider>
                  </ProfileProvider>
                </MealsProvider>
              </UserDataProvider>
            </IngredientsRefProvider>
          </FeatureFlagsProvider>
        </ThemeProvider>
      </AuthProvider>
    </TooltipProvider>
  )
}
